import { FC, useEffect, useState } from "react"
import { Outlet, useLocation } from "react-router-dom";
import { CommonHeader, useHeaderStore } from "../parts/CommonHeader";
import { isHeritageClause } from "typescript";
import { isLoggedIn, removeToken } from "../services/CookieHandler";
import { Subscription } from "../model/Subscription";
import { fetchAsync } from "../services/ApiHandler";
import { getNextPlanId, getPlanName, getPlanNameBySubscription } from "../services/SubscriptionService";
import { SubscriptionDialog, SubscriptionDialogProps } from "../parts/SubscriptionDialog";
import { useDisclosure } from "@mantine/hooks";
import { GenerationCreditDialog } from "../parts/GenerationCreditDialog";
import { GenerationCreditDialogProps } from "../parts/GenerationCreditDialog";
import { Modal } from "@mantine/core";
import { toast, Toaster } from "react-hot-toast";
import { CommonFooter } from "../parts/CommonFooter";
import { getModalOffset } from "../services/ScreenUtil";
import { getProfileImageUrl } from "../services/ProfileImageUtil";

export const AccountLayout: FC = () => {
  const { remainCount, setRemainCount, subscription, setSubscription, account, fetchAccount, screenWidth } = useHeaderStore();
  const [subscriptionDialogProps, setSubscriptionDialogProps] = useState<SubscriptionDialogProps | undefined>(undefined);
  const [loginFlag, setLoginFlag] = useState(isLoggedIn());
  const [generationCreditDialogProps, setGenerationCreditDialogProps] = useState<GenerationCreditDialogProps | undefined>(undefined);
  const [creditDialogOpened, { open, close }] = useDisclosure(false);
  const [subscriptionDialogOpened, { open: openSubscriptionDialog, close: closeSubscriptionDialog }] = useDisclosure(false);
  const { profileImage } = useHeaderStore();
  const location = useLocation();

  if (!isLoggedIn()) {
    window.location.href = '/login';
  }

  useEffect(() => {
    if (!isLoggedIn()) return;
    fetchSubscription();
    fetchAccount();
  }, []);

  function logout() {
    removeToken();
    window.location.href = '/image-generation'
  }

  const fetchSubscription = async () => {
    const response = await fetchAsync<Subscription>('Subscription/GetActiveSubscription');
    setSubscription(response);
  };
  
  function buildMenu(text: string, linkUrl: string, isActive: boolean, iconPath: string) {
    return <>
    <div className="mb-4 flex items-center w-[300px]">
      <img src={iconPath} className="mr-2 w-[30px] h-[30px]" />
      <a href={linkUrl}>{text}</a>
    </div>
    </>;
  }

  function onClickUpgradePlan() {
    setGenerationCreditDialogProps(undefined);
    const nextPlanId = getNextPlanId(subscription);
    setSubscriptionDialogProps({
      planId: nextPlanId
    } as SubscriptionDialogProps);
    openSubscriptionDialog();
  }

  function onClickCreditPurchase(){
    setGenerationCreditDialogProps({
      onPurchase: () => {
        toast.success("購入しました");
        setGenerationCreditDialogProps(undefined);
      }
    } as GenerationCreditDialogProps);
    open();
  }

  const isMobile = screenWidth < 640; // sm breakpoint
  const isGenerationHistoryPage = location.pathname === '/generation-history';
  const shouldHideHeader = isMobile && isGenerationHistoryPage;

  return  <>
    <Toaster />
    {!shouldHideHeader && <CommonHeader />}
    { generationCreditDialogProps !== undefined &&
      <Modal padding={0} size="auto" opened={creditDialogOpened} onClose={close} yOffset={getModalOffset(screenWidth)} closeButtonProps={{ className: 'mr-3' }}>
        <GenerationCreditDialog
         onPurchase={() => setGenerationCreditDialogProps(undefined)}
         onCancel={() => setGenerationCreditDialogProps(undefined)}
         onUpgrade={onClickUpgradePlan}
          />
      </Modal>
    }
    { subscriptionDialogProps !== undefined &&
      <Modal size="auto" opened={subscriptionDialogOpened} onClose={closeSubscriptionDialog} yOffset={getModalOffset(screenWidth)}>
        <SubscriptionDialog onPurchase={() => setSubscriptionDialogProps(undefined)} onCancel={() => setSubscriptionDialogProps(undefined)} planId={subscriptionDialogProps?.planId} />
      </Modal>
    }
    <div className="flex">
      <div id="left-menu" className="hidden sm:block ml-8 pt-4 max-h-[calc(100vh-76px)] overflow-scroll">
        <a href="/account" className="clickable">
          <div className="mb-4 flex items-center">
            <img src={getProfileImageUrl(profileImage)} className="h-[30px] w-[30px] mr-2 rounded-full" />
            <span className="text-xs text-gray-500">{account?.email}</span>
          </div>
        </a>
        {buildMenu('生成履歴', '/generation-history', false, '/images/icon/sample-image.svg')}
        {buildMenu('お支払い', '/subscription', false, '/images/icon/wallet.svg')}
        
        <div id="bottom-menu" className="ml-4 absolute bottom-4 left-0 right-0 w-[270px]">
          <div className="mb-4 flex justify-between items-center text-sm border rounded-lg p-2">
            <div>
              <div className="text-xs text-gray-500">現在のプラン</div>
              <div className="font-bold">{getPlanNameBySubscription(subscription)}</div>
            </div>
            { (subscription?.internalProductId === 'free' || subscription?.internalProductId.startsWith('lite') || subscription?.internalProductId.startsWith('standard')) &&
            <div onClick={onClickUpgradePlan} className="clickable font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#0000F3] to-[#F416BF]">プランをアップグレード</div>
            }
          </div>

          <div className="mb-4 flex justify-between items-center text-sm border rounded-lg p-2">
            <div>
              <div className="text-xs text-gray-500">残り生成回数</div>
              <div>
                <span className="font-bold mr-1">{remainCount}</span>
                <span className="text-gray-500 text-sm">回</span>
              </div>
            </div>
            { (subscription?.internalProductId.startsWith('lite') || subscription?.internalProductId.startsWith('standard')) &&  
            <div className="clickable" onClick={onClickCreditPurchase}>生成回数を追加 +</div>
            }
          </div>
          <div className="text-sm text-gray-500 pb-4 clickable ml-1" onClick={logout}>ログアウト</div>

        </div>  
      </div>
      <div className={`ml-0 sm:ml-8 w-full ${shouldHideHeader ? 'max-h-[100vh]' : 'max-h-[calc(100vh-76px)]'} overflow-scroll`}>
        <Outlet />
      </div>
    </div>
    <CommonFooter />
  </>
}
